<template>
  <a
    :href="fileUrl"
    target="_blank"
    rel="noopener noreferrer"
    class="download-card"
    :aria-label="`Descargar ${title}, archivo ${fileTypeLabel}`"
  >
    <div class="file-icon" aria-hidden="true">
      <i :class="iconClass"></i>
    </div>

    <div class="file-content">
      <span class="file-type">{{ fileTypeLabel }}</span>
      <h3 class="file-title">{{ title }}</h3>
      <p class="file-description">{{ description }}</p>

      <span class="download-action">
        Descargar documento
        <i class="bx bx-download" aria-hidden="true"></i>
      </span>
    </div>
  </a>
</template>

<script>
export default {
  name: "DownloadCard",
  props: {
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    fileUrl: {
      type: String,
      required: true,
    },
    fileType: {
      type: String,
      default: "file",
    },
  },
  computed: {
    iconClass() {
      switch (this.fileType) {
        case "pdf":
          return "bx bxs-file-pdf";
        case "xlsx":
          return "bx bxs-spreadsheet";
        default:
          return "bx bxs-file-blank";
      }
    },
    fileTypeLabel() {
      return this.fileType === "xlsx" ? "Excel · XLSX" : "Documento · PDF";
    },
  },
};
</script>

<style scoped>
.download-card {
  --accent-color: #ffffff;
  --accent-soft: rgba(230, 203, 114, 0.1);
  position: relative;
  display: flex;
  min-height: 230px;
  padding: 1.6rem;
  overflow: hidden;
  color: #ffffff;
  text-decoration: none;
  border: 1px solid rgba(230, 203, 114, 0.24);
  border-radius: 20px;
  background: rgba(6, 47, 54, 0.82);
  box-shadow: 0 16px 38px rgba(35, 39, 29, 0.14);
  transition: transform 0.25s ease, box-shadow 0.25s ease,
    border-color 0.25s ease;
}

.download-card::after {
  position: absolute;
  top: -65px;
  right: -65px;
  width: 150px;
  height: 150px;
  content: "";
  border-radius: 50%;
  background: var(--accent-soft);
  transition: transform 0.3s ease;
}

.download-card:hover,
.download-card:focus-visible {
  color: #ffffff;
  text-decoration: none;
  border-color: rgba(230, 203, 114, 0.48);
  box-shadow: 0 22px 46px rgba(43, 53, 46, 0.14);
  transform: translateY(-6px);
}

.download-card:hover::after,
.download-card:focus-visible::after {
  transform: scale(1.18);
}

.download-card:focus-visible {
  outline: 3px solid rgba(240, 211, 110, 0.35);
  outline-offset: 3px;
}

.file-icon {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 0 0 58px;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  margin-right: 1.15rem;
  color: var(--accent-color);
  font-size: 2rem;
  border-radius: 16px;
  background: var(--accent-soft);
}

.file-content {
  position: relative;
  z-index: 1;
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
}

.file-type {
  margin-bottom: 0.45rem;
  color: var(--accent-color);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.file-title {
  margin: 0 0 0.45rem;
  color: var(--bjp-gold-400);
  font-size: 1.1rem;
  font-weight: 750;
  line-height: 1.3;
}

.file-description {
  margin: 0 0 1.25rem;
  color: #ffffff;
  font-size: 0.9rem;
  line-height: 1.55;
}

.download-action {
  display: inline-flex;
  align-items: center;
  margin-top: auto;
  color: var(--bjp-gold-400);
  font-size: 0.86rem;
  font-weight: 750;
  gap: 0.45rem;
}

.download-action i {
  font-size: 1.2rem;
  transition: transform 0.2s ease;
}

.download-card:hover .download-action i {
  transform: translateY(3px);
}

@media (max-width: 575px) {
  .download-card {
    min-height: 205px;
    padding: 1.3rem;
  }
  .file-icon {
    flex-basis: 50px;
    width: 50px;
    height: 50px;
    margin-right: 0.9rem;
    font-size: 1.7rem;
  }
}

@media (max-width: 400px) {
  .download-card {
    min-height: 0;
    flex-direction: column;
    padding: 1.25rem;
  }
  .file-icon {
    flex-basis: 50px;
    margin-right: 0;
    margin-bottom: 1rem;
  }
  .file-description {
    margin-bottom: 1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .download-card,
  .download-card::after,
  .download-action i {
    transition: none;
  }
}
</style>
